<template>
  <div class="dashboard-container">
    <!-- Sidebar -->
    <Sidebar @open-contacts="goToEmailContacts" />

    <!-- Main Content -->
    <main class="main-content">
      <!-- TOPBAR -->
      <Topbar />

      <!-- Success/Error Messages -->
      <div v-if="successMessage" class="alert alert-success">
        {{ successMessage }}
      </div>
      <div v-if="errorMessage" class="alert alert-error">
        {{ errorMessage }}
      </div>

      <!-- Tabs nav (W1 Refonte #11 — aligned with invoxis-settings-v5-4.html) -->
      <div class="settings-tabs">
        <button class="settings-tab" :class="{ active: activeTab === 'company' }" @click="activeTab = 'company'">
          Company
        </button>
        <button class="settings-tab" :class="{ active: activeTab === 'platforms' }" @click="activeTab = 'platforms'">
          Platforms &amp; OTAs
        </button>
        <button class="settings-tab" :class="{ active: activeTab === 'reports' }" @click="activeTab = 'reports'">
          Reports
        </button>
        <button class="settings-tab" :class="{ active: activeTab === 'accounting' }" @click="activeTab = 'accounting'">
          Accounting
        </button>
        <button class="settings-tab" :class="{ active: activeTab === 'dac7' }" @click="activeTab = 'dac7'">
          DAC7
        </button>
        <button class="settings-tab" :class="{ active: activeTab === 'linked' }" @click="activeTab = 'linked'">
          Linked Properties
        </button>
        <!--
          Property tab retiré (retour client 2026-07-05) : duplique Properties > [property] > Financial settings.
          Le v-if="activeTab === 'property'" ci-dessous et toutes les méthodes associées restent en place au cas où on
          aurait besoin de récupérer un morceau plus tard. Aucun bouton n'y amène plus.
        -->
      </div>

      <!-- New (future) and wired tabs from mockup -->
      <CompanyTab v-if="activeTab === 'company'" />
      <PlatformsTab v-if="activeTab === 'platforms'" />
      <ReportsTab v-if="activeTab === 'reports'" />
      <AccountingTab v-if="activeTab === 'accounting'" />
      <Dac7Tab v-if="activeTab === 'dac7'" />

      <!-- Linked Properties tab -->
      <LinkedPropertiesTab v-if="activeTab === 'linked'" />

      <!-- Property Selection -->
      <div v-if="activeTab === 'property'" class="section-card">
        <div class="section-header">
          <h2>Property Configuration</h2>
          <p class="section-description">Configure calculation parameters for each property</p>
        </div>
        
        <div class="property-selector">
          <label class="selector-label">Select Property:</label>
          <select v-model="selectedPropertyId" @change="loadPropertySettings" class="property-select">
            <option value="">Choose a property...</option>
            <option v-for="property in properties" :key="property.id" :value="property.id">
              {{ property.name }}
            </option>
          </select>
        </div>
      </div>

      <!-- Property Settings Form -->
      <div v-if="activeTab === 'property' && selectedPropertyId && currentPropertySettings" class="section-card">
        <div class="section-header">
          <h2>{{ getSelectedPropertyName() }} - Settings</h2>
        </div>
        
        <div class="settings-grid">
          <!-- Management Fee Configuration -->
          <div class="setting-group full-width">
            <h3 class="subsection-title">Management Fee Configuration</h3>
          </div>

          <!-- Management Fee Rate -->
          <div class="setting-group">
            <label class="setting-label">Management Fee Rate (%)</label>
            <div class="input-group">
              <input 
                type="number" 
                v-model="currentPropertySettings.managementFeeRate" 
                @input="markAsModified"
                step="0.1" 
                min="0" 
                max="100"
                class="setting-input"
              >
              <span class="input-suffix">%</span>
            </div>
          </div>

          <!-- Management Fee Rate -->
          <div class="radio-group">
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="currentPropertySettings.managementFeeBase" 
      value="revenue"
      @change="markAsModified"
    >
    <span>Total Revenue (Gross Sales) - before any deductions</span>
  </label>
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="currentPropertySettings.managementFeeBase" 
      value="net"
      @change="markAsModified"
    >
    <span>Net Income - after platform fees and card fees</span>
  </label>
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="currentPropertySettings.managementFeeBase" 
      value="gross"
      @change="markAsModified"
    >
    <span>Gross Profit (after all direct expenses) - percentage</span>
  </label>
  <label class="radio-option">
    <input 
      type="radio" 
      v-model="currentPropertySettings.managementFeeBase" 
      value="grossFixed"
      @change="markAsModified"
    >
    <span>Gross Profit (after all direct expenses) - fixed amount</span>
  </label>
</div>
<p class="setting-description">
  Choose whether management fees are calculated on total revenue (highest), net income (medium), gross profit percentage, or a fixed monthly amount
</p>

          <!-- Fixed Management Fee Amount (conditional) -->
          <div v-if="currentPropertySettings.managementFeeBase === 'grossFixed'" class="setting-group">
            <label class="setting-label">
              Fixed Management Fee Amount (£)
              <span v-if="hasMonthSpecificFee" class="month-specific-badge" title="This value is specific to the selected month only">
                (Month-specific)
              </span>
            </label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                type="number"
                v-model="currentPropertySettings.managementFeeFixedAmount"
                @input="markAsModified"
                step="1"
                min="0"
                class="setting-input"
              >
            </div>
          </div>

          <!-- Month Selector (conditional - only for grossFixed) -->
          <div v-if="currentPropertySettings.managementFeeBase === 'grossFixed'" class="setting-group">
            <label class="setting-label">Apply to Specific Month (Optional)</label>
            <input
              type="month"
              v-model="selectedMonth"
              @change="onMonthChange"
              class="setting-input"
              placeholder="YYYY-MM"
            >
            <p class="setting-description">
              Leave empty to set as default for all months, or select a specific month to override just that month
            </p>
          </div>

          <!-- VAT Rate -->
          <div class="setting-group">
            <label class="setting-label">VAT Rate (%)</label>
            <div class="input-group">
              <input
                type="number"
                v-model="currentPropertySettings.vatRate"
                @input="markAsModified"
                step="0.1"
                min="0"
                max="100"
                class="setting-input"
              >
              <span class="input-suffix">%</span>
            </div>
          </div>

          <!-- VAT Business Model -->
          <div class="setting-group full-width">
            <label class="setting-label">VAT Business Model</label>
            <div class="radio-group">
              <label class="radio-option">
                <input
                  type="radio"
                  v-model="currentPropertySettings.vatBusinessModel"
                  value="agent"
                  @change="markAsModified"
                >
                <div class="radio-content">
                  <strong>Agent Only</strong>
                  <p class="radio-description">VAT applies only to management fees, consumables, and laundry services supplied by your company. Cheaper for clients.</p>
                </div>
              </label>
              <label class="radio-option">
                <input
                  type="radio"
                  v-model="currentPropertySettings.vatBusinessModel"
                  value="principal"
                  @change="markAsModified"
                >
                <div class="radio-content">
                  <strong>Principal (Full Control)</strong>
                  <p class="radio-description">VAT applies to all services including maintenance and cleaning. Your company takes full responsibility.</p>
                </div>
              </label>
            </div>
          </div>

          <!-- Laundry & Linen Supply - Fixed Monthly -->
          <div class="setting-group">
            <label class="setting-label">Laundry & Linen Supply - Fixed Monthly (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                type="number"
                v-model="currentPropertySettings.laundryFee"
                @input="markAsModified"
                step="1"
                min="0"
                class="setting-input"
              >
            </div>
            <p class="setting-description">Fixed monthly laundry cost (used when "Fixed" mode is selected in preview)</p>
          </div>

          <!-- Laundry Cost per Booking -->
          <div class="setting-group">
            <label class="setting-label">Laundry Cost per Booking (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                type="number"
                v-model="currentPropertySettings.laundryPerBooking"
                @input="markAsModified"
                step="0.01"
                min="0"
                class="setting-input"
              >
            </div>
            <p class="setting-description">Cost per changeover (used when "Per Changeover" mode is selected in preview)</p>
          </div>

          <!-- Laundry VAT Applicable -->
          <div class="setting-group">
            <label class="setting-label">Apply VAT on Laundry</label>
            <div class="toggle-container">
              <label class="toggle-switch">
                <input
                  type="checkbox"
                  v-model="currentPropertySettings.laundryVatApplicable"
                  @change="markAsModified"
                >
                <span class="toggle-slider"></span>
              </label>
              <span class="toggle-label">{{ currentPropertySettings.laundryVatApplicable ? 'VAT Applied' : 'No VAT' }}</span>
            </div>
            <p class="setting-description">Enable if laundry service is supplied by your company (Agent model)</p>
          </div>

          <!-- Monthly Costs Section -->
          <div class="setting-group full-width">
            <h3 class="subsection-title">Monthly Variable Costs</h3>
          </div>

          <!-- Maintenance -->
          <div class="setting-group">
            <label class="setting-label">Maintenance (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input 
                type="number" 
                v-model="currentPropertySettings.maintenance" 
                @input="markAsModified"
                step="1" 
                min="0"
                class="setting-input"
              >
            </div>
          </div>

          <!-- Consumables -->
          <div class="setting-group">
            <label class="setting-label">Consumables (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input 
                type="number" 
                v-model="currentPropertySettings.consumables" 
                @input="markAsModified"
                step="1" 
                min="0"
                class="setting-input"
              >
            </div>
          </div>

          <!-- Other Expenses -->
          <div class="setting-group">
            <label class="setting-label">Other Expenses (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input 
                type="number" 
                v-model="currentPropertySettings.otherExpenses" 
                @input="markAsModified"
                step="1" 
                min="0"
                class="setting-input"
              >
            </div>
          </div>

          <!-- Cleaning Fee Configuration -->
          <div class="setting-group full-width">
            <h3 class="subsection-title">Cleaning Fee Configuration</h3>
          </div>

          <!-- Cleaning Fee - Fixed Monthly -->
          <div class="setting-group">
            <label class="setting-label">Cleaning Fee - Fixed Monthly (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                type="number"
                v-model="currentPropertySettings.cleaningFee"
                @input="markAsModified"
                step="1"
                min="0"
                class="setting-input"
              >
            </div>
            <p class="setting-description">Fixed monthly cleaning cost (used when "Fixed Monthly Amount" mode is selected in preview)</p>
          </div>

          <!-- Cleaning Cost per Booking -->
          <div class="setting-group">
            <label class="setting-label">Cleaning Cost per Booking (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                type="number"
                v-model="currentPropertySettings.cleaningPerBooking"
                @input="markAsModified"
                step="0.01"
                min="0"
                class="setting-input"
              >
            </div>
            <p class="setting-description">Cost per changeover (used when "Per Changeover" mode is selected in preview)</p>
          </div>

          <!-- Custom Rates per Platform (for 'monthly' mode in preview) -->
          <div class="setting-group">
            <label class="setting-label">Booking.com Cleaning Fee (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                type="number"
                v-model="currentPropertySettings.bookingCleaningFee"
                @input="markAsModified"
                step="1"
                min="0"
                class="setting-input"
              >
              <span class="input-suffix">per booking</span>
            </div>
            <p class="setting-description">Used when "Custom Rates per Platform" mode is selected in preview</p>
          </div>

          <div class="setting-group">
            <label class="setting-label">Other Platforms Cleaning Fee (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                type="number"
                v-model="currentPropertySettings.otherCleaningFee"
                @input="markAsModified"
                step="1"
                min="0"
                class="setting-input"
              >
              <span class="input-suffix">per booking</span>
            </div>
            <p class="setting-description">
              Applied to Airbnb, VRBO, Direct bookings (used when "Custom Rates per Platform" mode is selected in preview)
            </p>
          </div>

          <!-- Cleaning VAT Applicable -->
          <div class="setting-group">
            <label class="setting-label">Apply VAT on Cleaning</label>
            <div class="toggle-container">
              <label class="toggle-switch">
                <input
                  type="checkbox"
                  v-model="currentPropertySettings.cleaningVatApplicable"
                  @change="markAsModified"
                >
                <span class="toggle-slider"></span>
              </label>
              <span class="toggle-label">{{ currentPropertySettings.cleaningVatApplicable ? 'VAT Applied' : 'No VAT' }}</span>
            </div>
            <p class="setting-description">Enable if cleaning service is supplied by your company (Principal model)</p>
          </div>

          <!-- Card Fee Configuration -->
          <div class="setting-group full-width">
            <h3 class="subsection-title">Card Fee Configuration</h3>
          </div>

          <div class="setting-group full-width">
            <label class="setting-label">Card Fee Mode</label>
            <div class="radio-group">
              <label class="radio-option">
                <input 
                  type="radio" 
                  v-model="currentPropertySettings.cardFeeMode" 
                  value="rentlio"
                  @change="markAsModified"
                >
                <span>Use Rentlio Data (Booking.com only)</span>
              </label>
              <label class="radio-option">
                <input 
                  type="radio" 
                  v-model="currentPropertySettings.cardFeeMode" 
                  value="custom"
                  @change="markAsModified"
                >
                <span>Custom Calculation</span>
              </label>
            </div>
          </div>

          <div v-if="currentPropertySettings.cardFeeMode === 'custom'" class="setting-group">
            <label class="setting-label">Card Fee Rate (%)</label>
            <div class="input-group">
              <input 
                type="number" 
                v-model="currentPropertySettings.customCardFeeRate" 
                @input="markAsModified"
                step="0.01" 
                min="0" 
                max="10"
                class="setting-input"
              >
              <span class="input-suffix">%</span>
            </div>
          </div>

          <div v-if="currentPropertySettings.cardFeeMode === 'custom'" class="setting-group">
            <label class="setting-label">Card Fee Fixed (£)</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input 
                type="number" 
                v-model="currentPropertySettings.customCardFeeFixed" 
                @input="markAsModified"
                step="0.01" 
                min="0"
                class="setting-input"
              >
            </div>
          </div>

          <!-- Platform Commission Configuration -->
          <div class="setting-group full-width">
            <h3 class="subsection-title">Platform Commission Configuration</h3>
          </div>

          <div class="setting-group full-width">
            <label class="setting-label">Commission Mode</label>
            <div class="radio-group">
              <label class="radio-option">
                <input
                  type="radio"
                  v-model="currentPropertySettings.platformFeeMode"
                  value="rentlio"
                  @change="markAsModified"
                >
                <span>Use Rentlio Data</span>
              </label>
              <label class="radio-option">
                <input
                  type="radio"
                  v-model="currentPropertySettings.platformFeeMode"
                  value="custom"
                  @change="markAsModified"
                >
                <span>Custom Calculation by Platform</span>
              </label>
            </div>
          </div>

          <!-- Booking.com Custom Commission -->
          <div v-if="currentPropertySettings.platformFeeMode === 'custom'" class="setting-group full-width">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="currentPropertySettings.applyCustomBookingCom"
                @change="markAsModified"
              >
              <span>Apply custom commission for Booking.com</span>
            </label>
          </div>

          <div v-if="currentPropertySettings.platformFeeMode === 'custom' && currentPropertySettings.applyCustomBookingCom" class="setting-group">
            <label class="setting-label">Booking.com Commission Rate (%)</label>
            <div class="input-group">
              <input
                type="number"
                v-model="currentPropertySettings.bookingComCommissionRate"
                @input="markAsModified"
                step="0.01"
                min="0"
                max="100"
                class="setting-input"
              >
              <span class="input-suffix">%</span>
            </div>
            <small class="help-text">Applied on total amount</small>
          </div>

          <!-- Airbnb Custom Commission -->
          <div v-if="currentPropertySettings.platformFeeMode === 'custom'" class="setting-group full-width">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="currentPropertySettings.applyCustomAirbnb"
                @change="markAsModified"
              >
              <span>Apply custom commission for Airbnb</span>
            </label>
          </div>

          <div v-if="currentPropertySettings.platformFeeMode === 'custom' && currentPropertySettings.applyCustomAirbnb" class="setting-group">
            <label class="setting-label">Airbnb Commission Rate (%)</label>
            <div class="input-group">
              <input
                type="number"
                v-model="currentPropertySettings.airbnbCommissionRate"
                @input="markAsModified"
                step="0.01"
                min="0"
                max="100"
                class="setting-input"
              >
              <span class="input-suffix">%</span>
            </div>
            <small class="help-text">Applied on total amount</small>
          </div>

          <!-- Other Platforms Custom Commission -->
          <div v-if="currentPropertySettings.platformFeeMode === 'custom'" class="setting-group full-width">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="currentPropertySettings.applyCustomOther"
                @change="markAsModified"
              >
              <span>Apply custom commission for Other Platforms</span>
            </label>
          </div>

          <div v-if="currentPropertySettings.platformFeeMode === 'custom' && currentPropertySettings.applyCustomOther" class="setting-group">
            <label class="setting-label">Other Platforms Commission Rate (%)</label>
            <div class="input-group">
              <input
                type="number"
                v-model="currentPropertySettings.otherPlatformCommissionRate"
                @input="markAsModified"
                step="0.01"
                min="0"
                max="100"
                class="setting-input"
              >
              <span class="input-suffix">%</span>
            </div>
            <small class="help-text">Applied on total amount</small>
          </div>
        </div>

        <!-- Save Actions -->
        <div class="actions-section">
          <div class="actions-card">
            <div class="actions-content">
              <div class="actions-info">
                <h3>Save Configuration</h3>
                <p v-if="hasUnsavedChanges" class="warning-text">You have unsaved changes for {{ getSelectedPropertyName() }}</p>
                <p v-else class="success-text">All changes saved</p>
              </div>
              <div class="actions-buttons">
                <button @click="resetPropertySettings" class="btn btn-outline" :disabled="!hasUnsavedChanges">
                  Reset to Default
                </button>
                <button @click="savePropertySettings" class="btn btn-primary" :disabled="!hasUnsavedChanges || saving">
                  <span v-if="saving">Saving...</span>
                  <span v-else>Save Changes</span>
                </button>
                <button
                  v-if="currentPropertySettings && currentPropertySettings.managementFeeBase === 'grossFixed' && hasFixedAmountChanged && selectedMonth"
                  @click="saveForThisMonthOnly"
                  :disabled="!hasFixedAmountChanged || saving"
                  class="btn btn-secondary"
                  title="Save this fixed fee amount for the selected month only, without affecting other months"
                >
                  <span v-if="saving">Saving...</span>
                  <span v-else>Save for Selected Month Only</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Owner Information Section (Xero Integration - Milestone 1) -->
      <div v-if="activeTab === 'property' && selectedPropertyId" class="section-card owner-section">
        <div class="section-header">
          <h2>👤 Owner Information</h2>
          <p class="section-description">
            Configure the property owner's details. The primary email will be used for all monthly reports.
          </p>
        </div>

        <!-- Success Message -->
        <div v-if="successMessage" class="alert alert-success">
          {{ successMessage }}
        </div>

        <div class="settings-grid">
          <div class="setting-group full-width">
            <label class="setting-label">Full Legal Name</label>
            <input
              type="text"
              v-model="ownerData.full_legal_name"
              placeholder="e.g., John Michael Smith"
              class="setting-input"
            >
          </div>

          <div class="setting-group">
            <label class="setting-label required">Primary Email</label>
            <input
              type="email"
              v-model="ownerData.primary_email"
              placeholder="e.g., john.smith@example.com"
              class="setting-input"
            >
            <span class="helper-text warning">
              ⚠️ All monthly reports will be sent to this email
            </span>
          </div>

          <div class="setting-group">
            <label class="setting-label">Phone Number</label>
            <input
              type="tel"
              v-model="ownerData.phone_number"
              placeholder="e.g., +44 7700 900000"
              class="setting-input"
            >
          </div>

          <div class="setting-group">
            <label class="setting-label">Date of Birth</label>
            <input
              type="date"
              v-model="ownerData.date_of_birth"
              class="setting-input"
            >
          </div>

          <div class="setting-group">
            <label class="setting-label">National Insurance Number</label>
            <input
              type="text"
              v-model="ownerData.national_insurance_number"
              placeholder="e.g., AB123456C"
              class="setting-input"
              maxlength="9"
            >
          </div>

          <div class="setting-group full-width">
            <label class="setting-label">Address Line 1</label>
            <input
              type="text"
              v-model="ownerData.address_line_1"
              placeholder="e.g., 123 Main Street"
              class="setting-input"
            >
          </div>

          <div class="setting-group full-width">
            <label class="setting-label">Address Line 2</label>
            <input
              type="text"
              v-model="ownerData.address_line_2"
              placeholder="e.g., Apartment 4B"
              class="setting-input"
            >
          </div>

          <div class="setting-group">
            <label class="setting-label">City</label>
            <input
              type="text"
              v-model="ownerData.city"
              placeholder="e.g., London"
              class="setting-input"
            >
          </div>

          <div class="setting-group">
            <label class="setting-label">Postal Code</label>
            <input
              type="text"
              v-model="ownerData.postal_code"
              placeholder="e.g., SW1A 1AA"
              class="setting-input"
            >
          </div>

          <div class="setting-group">
            <label class="setting-label">Country</label>
            <select v-model="ownerData.country" class="setting-input">
              <option value="GB">United Kingdom</option>
              <option value="IE">Ireland</option>
              <option value="US">United States</option>
              <option value="FR">France</option>
              <option value="DE">Germany</option>
              <option value="ES">Spain</option>
              <option value="IT">Italy</option>
            </select>
          </div>
        </div>

        <!-- Error Message (above buttons) -->
        <div v-if="errorMessage" class="error-message-above-button">
          {{ errorMessage }}
        </div>

        <div class="button-row">
          <button
            @click="saveOwnerInformation"
            class="btn btn-primary"
            :disabled="savingOwner"
          >
            <span v-if="savingOwner">Saving...</span>
            <span v-else>{{ ownerExists ? 'Update Owner Information' : 'Save Owner Information' }}</span>
          </button>

          <button
            v-if="ownerExists"
            @click="deleteOwnerInformation"
            class="btn btn-danger"
            :disabled="deletingOwner"
          >
            <span v-if="deletingOwner">Deleting...</span>
            <span v-else>Delete Owner</span>
          </button>
        </div>
      </div>

      <!-- Instructions -->
      <div v-if="activeTab === 'property' && !selectedPropertyId" class="section-card">
        <div class="empty-state">
          <div class="empty-icon">🏨</div>
          <h3>Select a Property</h3>
          <p>Choose a property above to configure its calculation parameters</p>
        </div>
      </div>
    </main>

    <!-- Notification Modal -->
    <div v-if="showNotificationModal" class="notification-modal-overlay" @click="closeNotificationModal">
      <div class="notification-modal-container" @click.stop>
        <div class="notification-modal-header" :class="notificationData.type">
          <div class="notification-icon-circle" :class="notificationData.type">
            <span class="notification-icon">{{ notificationData.icon }}</span>
          </div>
          <button @click="closeNotificationModal" class="modal-close-btn">
            <span>&times;</span>
          </button>
        </div>
        <div class="notification-modal-body">
          <h2 class="notification-title">{{ notificationData.title }}</h2>
          <p class="notification-message" v-html="notificationData.message"></p>
        </div>
        <div class="notification-modal-footer">
          <button @click="closeNotificationModal" class="notification-btn" :class="notificationData.type">
            OK
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SETTINGS from '@/config/settings.js';
import Sidebar from '@/components/Sidebar.vue';
import Topbar from '@/components/Topbar.vue';
import LinkedPropertiesTab from '@/components/LinkedPropertiesTab.vue';
import CompanyTab from '@/components/settings/CompanyTab.vue';
import PlatformsTab from '@/components/settings/PlatformsTab.vue';
import ReportsTab from '@/components/settings/ReportsTab.vue';
import AccountingTab from '@/components/settings/AccountingTab.vue';
import Dac7Tab from '@/components/settings/Dac7Tab.vue';

export default {
  name: 'SettingsView',
  components: {
    Sidebar,
    Topbar,
    LinkedPropertiesTab,
    CompanyTab,
    PlatformsTab,
    ReportsTab,
    AccountingTab,
    Dac7Tab,
  },
  provide() {
    return {
      notify: (type, title, message) => this.showNotification(type, title, message),
    };
  },
  data() {
    return {
      isCollapsed: false,
      hasUnsavedChanges: false,
      saving: false,
      successMessage: '',
      errorMessage: '',
      showNotificationModal: false,
      notificationData: { type: 'info', icon: 'ℹ️', title: '', message: '' },

      // W2.2 — tabs nav (mockup invoxis-settings-v4-26.html)
      activeTab: 'company', // 'company' | 'platforms' | 'reports' | 'accounting' | 'dac7' | 'linked' | 'property'

      // Properties data
      properties: [],
      selectedPropertyId: '',
      currentPropertySettings: null,
      originalSettings: {}, // Pour comparer les changements
      hasMonthSpecificFee: false, // Indique si un montant spécifique au mois est chargé
      selectedMonth: '', // Format: 'YYYY-MM' pour le sélecteur de mois

      // Default settings template - UPDATED WITH NEW VAT FIELDS
      defaultSettings: {
        managementFeeRate: 16,
        managementFeeBase: 'net', // 'revenue', 'net', 'gross', 'grossFixed'
        managementFeeFixedAmount: 500, // Fixed amount for grossFixed mode
        vatRate: 20,
        vatBusinessModel: 'agent', // 'agent' or 'principal'
        laundryFee: 0, // Monthly laundry & linen supply cost
        laundryCalculationMode: 'fixed', // 'fixed' or 'per_booking'
        laundryPerBooking: 15.00, // Cost per booking for per_booking mode
        laundryVatApplicable: true, // Apply VAT on laundry services
        maintenance: 0,
        consumables: 20,
        otherExpenses: 0,
        cleaningMode: 'rentlio',
        cleaningFee: 0, // Fixed monthly cleaning cost
        cleaningPerBooking: 150.00, // Cost per booking for per_booking mode
        bookingCleaningFee: 150, // For 'monthly' mode (Custom Rates) - Booking.com
        otherCleaningFee: 120, // For 'monthly' mode (Custom Rates) - Other platforms
        cleaningVatApplicable: false, // Apply VAT on cleaning services
        cardFeeMode: 'rentlio',
        customCardFeeRate: 2.9,
        customCardFeeFixed: 0.30,
        platformFeeMode: 'rentlio',
        applyCustomBookingCom: false,
        bookingComCommissionRate: 15.00,
        applyCustomAirbnb: false,
        airbnbCommissionRate: 3.00,
        applyCustomOther: false,
        otherPlatformCommissionRate: 10.00
      },

      // Owner Information (Xero Integration - Milestone 1)
      ownerData: {
        full_legal_name: '',
        primary_email: '',
        phone_number: '',
        date_of_birth: '',
        national_insurance_number: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        postal_code: '',
        country: 'GB'
      },
      ownerExists: false,
      savingOwner: false,
      deletingOwner: false,

      baseUrl: SETTINGS.API.baseUrl
    };
  },

  computed: {
    hasFixedAmountChanged() {
      // Vérifie si seul le fixed amount a changé (pas les autres paramètres)
      if (!this.hasUnsavedChanges || !this.currentPropertySettings || this.currentPropertySettings.managementFeeBase !== 'grossFixed') {
        return false;
      }
      // Compare uniquement le fixed amount avec l'original
      return parseFloat(this.currentPropertySettings.managementFeeFixedAmount) !== parseFloat(this.originalSettings.managementFeeFixedAmount);
    }
  },
  
  methods: {
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
    },
    
    logout() {
      localStorage.removeItem('token');
      this.$router.push('/login');
    },
    
    markAsModified() {
      this.hasUnsavedChanges = true;
      this.clearMessages();
    },
    
    clearMessages() {
      this.successMessage = '';
      this.errorMessage = '';
    },

    showNotification(type, title, message) {
      const icons = { success: '✓', error: '✕', warning: '!', info: 'i' };
      this.notificationData = {
        type,
        icon: icons[type] || icons.info,
        title,
        message,
      };
      this.showNotificationModal = true;
    },

    closeNotificationModal() {
      this.showNotificationModal = false;
    },

    getSelectedPropertyName() {
      const property = this.properties.find(p => p.id === this.selectedPropertyId);
      return property ? property.name : '';
    },
    
    async fetchProperties() {
      const token = localStorage.getItem('token');
      if (!token) {
        this.$router.push('/login');
        return;
      }

      const headers = { 'Authorization': `Bearer ${token}` };

      try {
        // 1. Récupérer la config PMS de l'utilisateur
        const configResponse = await axios.get(`${this.baseUrl}/api/users/pms-configs/`, { headers });

        // L'API retourne une réponse paginée: { count, results: [...] }
        const pmsConfigs = configResponse.data.results || configResponse.data;

        if (!pmsConfigs || !Array.isArray(pmsConfigs) || pmsConfigs.length === 0) {
          console.error('No PMS configuration found');
          this.errorMessage = 'No PMS configuration found. Please configure your PMS connection first.';
          this.properties = [];
          return;
        }

        const pmsConfigId = pmsConfigs[0].id;

        // 2. Récupérer les propriétés avec le pms_config_id
        const response = await axios.get(
          `${this.baseUrl}/api/pms/properties/?pms_config_id=${pmsConfigId}`,
          { headers }
        );

        // 3. Gérer la structure de réponse
        if (response.data.status === 'success') {
          this.properties = response.data.properties.data || [];
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
        } else {
          this.errorMessage = 'Failed to load properties. Please try again.';
        }
      }
    },
    
    async loadPropertySettings() {
      if (!this.selectedPropertyId) {
        this.currentPropertySettings = null;
        this.hasUnsavedChanges = false;
        this.selectedMonth = '';
        this.hasMonthSpecificFee = false;
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        this.$router.push('/login');
        return;
      }

      const headers = { 'Authorization': `Bearer ${token}` };

      try {
        const response = await axios.get(
          `${this.baseUrl}/api/pms/settings/${this.selectedPropertyId}/`,
          { headers }
        );

        if (response.data.success) {
          // Map backend field names to frontend (snake_case to camelCase)
          const backendData = response.data.data;
          this.currentPropertySettings = {
            managementFeeRate: parseFloat(backendData.management_fee_rate || backendData.managementFeeRate || 16),
            managementFeeBase: backendData.management_fee_base || backendData.managementFeeBase || 'net',
            managementFeeFixedAmount: parseFloat(backendData.management_fee_fixed_amount || backendData.managementFeeFixedAmount || 500),
            vatRate: parseFloat(backendData.vat_rate || backendData.vatRate || 20),
            vatBusinessModel: backendData.vat_business_model || backendData.vatBusinessModel || 'agent',
            laundryFee: parseFloat(backendData.laundry_fee || backendData.laundryFee || 0),
            laundryCalculationMode: backendData.laundry_calculation_mode || backendData.laundryCalculationMode || 'fixed',
            laundryPerBooking: parseFloat(backendData.laundry_per_booking || backendData.laundryPerBooking || 15.00),
            laundryVatApplicable: backendData.laundry_vat_applicable !== undefined ? backendData.laundry_vat_applicable : (backendData.laundryVatApplicable !== undefined ? backendData.laundryVatApplicable : true),
            maintenance: parseFloat(backendData.maintenance || 0),
            consumables: parseFloat(backendData.consumables || 20),
            otherExpenses: parseFloat(backendData.other_expenses || backendData.otherExpenses || 0),
            cleaningMode: backendData.cleaning_mode || backendData.cleaningMode || 'rentlio',
            cleaningFee: parseFloat(backendData.cleaning_fee || backendData.cleaningFee || 0),
            cleaningPerBooking: parseFloat(backendData.cleaning_per_booking || backendData.cleaningPerBooking || 150.00),
            bookingCleaningFee: parseFloat(backendData.booking_cleaning_fee || backendData.bookingCleaningFee || 150),
            otherCleaningFee: parseFloat(backendData.other_cleaning_fee || backendData.otherCleaningFee || 120),
            cleaningVatApplicable: backendData.cleaning_vat_applicable !== undefined ? backendData.cleaning_vat_applicable : (backendData.cleaningVatApplicable !== undefined ? backendData.cleaningVatApplicable : false),
            cardFeeMode: backendData.card_fee_mode || backendData.cardFeeMode || 'rentlio',
            customCardFeeRate: parseFloat(backendData.custom_card_fee_rate || backendData.customCardFeeRate || 2.9),
            customCardFeeFixed: parseFloat(backendData.custom_card_fee_fixed || backendData.customCardFeeFixed || 0.30),
            platformFeeMode: backendData.platform_fee_mode || backendData.platformFeeMode || 'rentlio',
            applyCustomBookingCom: backendData.apply_custom_booking_com || backendData.applyCustomBookingCom || false,
            bookingComCommissionRate: parseFloat(backendData.booking_com_commission_rate || backendData.bookingComCommissionRate || 15.00),
            applyCustomAirbnb: backendData.apply_custom_airbnb || backendData.applyCustomAirbnb || false,
            airbnbCommissionRate: parseFloat(backendData.airbnb_commission_rate || backendData.airbnbCommissionRate || 3.00),
            applyCustomOther: backendData.apply_custom_other || backendData.applyCustomOther || false,
            otherPlatformCommissionRate: parseFloat(backendData.other_platform_commission_rate || backendData.otherPlatformCommissionRate || 10.00)
          };
        }
      } catch (error) {
        console.error('Error loading property settings:', error);
        // Use defaults on error
        this.currentPropertySettings = { ...this.defaultSettings };
      }

      // Store original settings for comparison
      this.originalSettings = { ...this.currentPropertySettings };
      this.hasUnsavedChanges = false;
      this.selectedMonth = '';
      this.hasMonthSpecificFee = false;
      this.clearMessages();

      // Load owner information for this property (Xero Integration - Milestone 1)
      await this.loadOwnerInformation();
    },
    
    async savePropertySettings() {
      if (!this.selectedPropertyId || !this.currentPropertySettings) return;

      this.saving = true;
      this.clearMessages();

      try {
        // Validate settings
        if (!this.validateSettings()) {
          this.showNotification('warning', 'Invalid values', 'Please check your settings values.');
          return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const headers = { 'Authorization': `Bearer ${token}` };

        // Map frontend field names to backend (camelCase to snake_case)
        // Ensure all numeric values are properly converted to numbers
        const payload = {
          property_id: this.selectedPropertyId,
          management_fee_rate: parseFloat(this.currentPropertySettings.managementFeeRate) || 0,
          management_fee_base: this.currentPropertySettings.managementFeeBase,
          management_fee_fixed_amount: parseFloat(this.currentPropertySettings.managementFeeFixedAmount) || 0,
          vat_rate: parseFloat(this.currentPropertySettings.vatRate) || 0,
          vat_business_model: this.currentPropertySettings.vatBusinessModel,
          laundry_fee: parseFloat(this.currentPropertySettings.laundryFee) || 0,
          laundry_calculation_mode: this.currentPropertySettings.laundryCalculationMode,
          laundry_per_booking: parseFloat(this.currentPropertySettings.laundryPerBooking) || 0,
          laundry_vat_applicable: this.currentPropertySettings.laundryVatApplicable,
          maintenance: parseFloat(this.currentPropertySettings.maintenance) || 0,
          consumables: parseFloat(this.currentPropertySettings.consumables) || 0,
          other_expenses: parseFloat(this.currentPropertySettings.otherExpenses) || 0,
          cleaning_mode: this.currentPropertySettings.cleaningMode,
          cleaning_fee: parseFloat(this.currentPropertySettings.cleaningFee) || 0,
          cleaning_per_booking: parseFloat(this.currentPropertySettings.cleaningPerBooking) || 0,
          booking_cleaning_fee: parseFloat(this.currentPropertySettings.bookingCleaningFee) || 0,
          other_cleaning_fee: parseFloat(this.currentPropertySettings.otherCleaningFee) || 0,
          cleaning_vat_applicable: this.currentPropertySettings.cleaningVatApplicable,
          card_fee_mode: this.currentPropertySettings.cardFeeMode,
          custom_card_fee_rate: parseFloat(this.currentPropertySettings.customCardFeeRate) || 0,
          custom_card_fee_fixed: parseFloat(this.currentPropertySettings.customCardFeeFixed) || 0,
          platform_fee_mode: this.currentPropertySettings.platformFeeMode,
          apply_custom_booking_com: this.currentPropertySettings.applyCustomBookingCom,
          booking_com_commission_rate: parseFloat(this.currentPropertySettings.bookingComCommissionRate) || 0,
          apply_custom_airbnb: this.currentPropertySettings.applyCustomAirbnb,
          airbnb_commission_rate: parseFloat(this.currentPropertySettings.airbnbCommissionRate) || 0,
          apply_custom_other: this.currentPropertySettings.applyCustomOther,
          other_platform_commission_rate: parseFloat(this.currentPropertySettings.otherPlatformCommissionRate) || 0
        };

        const response = await axios.post(
          `${this.baseUrl}/api/pms/settings/`,
          payload,
          { headers }
        );

        if (response.data.success) {
          this.hasUnsavedChanges = false;
          this.showNotification('success', 'Settings saved', `Settings updated for ${this.getSelectedPropertyName()}.`);
        } else {
          this.showNotification('error', 'Save failed', response.data.message || 'Failed to save settings.');
        }

      } catch (error) {
        console.error('Error saving settings:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
        } else {
          this.showNotification('error', 'Save failed', error.response?.data?.message || 'Failed to save settings. Please try again.');
        }
      } finally {
        this.saving = false;
      }
    },

    validateSettings() {
      if (!this.currentPropertySettings) return false;

      const s = this.currentPropertySettings;

      // Check percentage rates
      if (s.managementFeeRate < 0 || s.managementFeeRate > 100) return false;
      if (s.vatRate < 0 || s.vatRate > 100) return false;
      if (s.customCardFeeRate < 0 || s.customCardFeeRate > 10) return false;

      // Check monetary values
      if (s.maintenance < 0 || s.consumables < 0 || s.otherExpenses < 0) return false;
      if (s.monthlyCleaningFee < 0 || s.customCardFeeFixed < 0) return false;
      if (s.managementFeeFixedAmount < 0) return false;
      if (s.laundryFee < 0) return false;
      if (s.cleaningFee < 0 || s.cleaningPerBooking < 0) return false;

      // Check management fee base
      if (!['gross', 'net', 'revenue', 'grossFixed'].includes(s.managementFeeBase)) return false;

      return true;
    },
    
    resetPropertySettings() {
      if (!this.selectedPropertyId) return;
      
      if (confirm(`Reset all settings for ${this.getSelectedPropertyName()} to default values?`)) {
        this.currentPropertySettings = { ...this.defaultSettings };
        this.hasUnsavedChanges = true;
        this.clearMessages();
      }
    },

    async onMonthChange() {
      // Quand l'utilisateur sélectionne un mois, vérifier s'il existe un montant spécifique
      if (this.selectedMonth && this.selectedPropertyId) {
        const [year, month] = this.selectedMonth.split('-');
        await this.checkMonthSpecificFee(this.selectedPropertyId, parseInt(year), parseInt(month));
      } else {
        // Revenir aux valeurs par défaut
        this.hasMonthSpecificFee = false;
        await this.loadPropertySettings();
      }
    },

    async checkMonthSpecificFee(propertyId, year, month) {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const headers = { 'Authorization': `Bearer ${token}` };

        // Appeler l'API pour vérifier s'il existe un montant spécifique
        const response = await axios.get(
          `${this.baseUrl}/api/pms/report-fixed-fee/`,
          {
            params: { property_id: propertyId, year: year, month: month },
            headers
          }
        );

        if (response.data.success && response.data.data && response.data.data.fixed_amount !== null) {
          // Un montant spécifique existe pour ce mois
          this.currentPropertySettings.managementFeeFixedAmount = parseFloat(response.data.data.fixed_amount);
          this.originalSettings.managementFeeFixedAmount = parseFloat(response.data.data.fixed_amount);
          this.hasMonthSpecificFee = true;
          this.hasUnsavedChanges = false;
          console.log(`Month-specific fee loaded: £${response.data.data.fixed_amount} for ${year}-${month}`);
        } else {
          // Pas de montant spécifique, utiliser le défaut
          this.hasMonthSpecificFee = false;
        }

      } catch (error) {
        console.error('Error checking month-specific fee:', error);
        this.hasMonthSpecificFee = false;
      }
    },

    async saveForThisMonthOnly() {
      if (!this.selectedPropertyId || !this.currentPropertySettings || !this.selectedMonth) return;

      this.saving = true;
      this.clearMessages();

      try {
        const [year, month] = this.selectedMonth.split('-');

        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const headers = { 'Authorization': `Bearer ${token}` };

        // Payload pour sauvegarder le fixed fee pour ce mois uniquement
        const payload = {
          property_id: this.selectedPropertyId,
          year: parseInt(year),
          month: parseInt(month),
          fixed_amount: this.currentPropertySettings.managementFeeFixedAmount
        };

        const response = await axios.post(
          `${this.baseUrl}/api/pms/report-fixed-fee/`,
          payload,
          { headers }
        );

        if (response.data.success) {
          this.hasMonthSpecificFee = true;
          this.originalSettings.managementFeeFixedAmount = this.currentPropertySettings.managementFeeFixedAmount;
          this.hasUnsavedChanges = false;

          const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                             'July', 'August', 'September', 'October', 'November', 'December'];
          const monthName = monthNames[parseInt(month) - 1];

          this.showNotification(
            'success',
            'Fixed fee saved',
            `Fixed Management Fee of £${this.currentPropertySettings.managementFeeFixedAmount} saved for ${monthName} ${year} only. Other months keep the default setting.`
          );
        } else {
          this.showNotification('error', 'Save failed', response.data.message || 'Failed to save month-specific fee.');
        }

      } catch (error) {
        console.error('Error saving month-specific fee:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
        } else {
          this.showNotification('error', 'Save failed', error.response?.data?.message || 'Failed to save month-specific fee. Please try again.');
        }
      } finally {
        this.saving = false;
      }
    },

    goToEmailContacts() {
      this.$router.push('/reservations?openContacts=true');
    },

    // ========================================
    // OWNER INFORMATION METHODS (Xero Integration - Milestone 1)
    // ========================================

    async loadOwnerInformation() {
      if (!this.selectedPropertyId) return;

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${this.baseUrl}/api/pms/property-owner/${this.selectedPropertyId}/`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success && response.data.data) {
          this.ownerData = response.data.data;
          this.ownerExists = true;
        } else {
          // Reset form if no owner exists
          this.resetOwnerForm();
          this.ownerExists = false;
        }
      } catch (error) {
        console.error('Error loading owner information:', error);
        this.ownerExists = false;
      }
    },

    async saveOwnerInformation() {
      // Validation
      if (!this.validateOwnerForm()) {
        return;
      }

      this.savingOwner = true;

      try {
        const token = localStorage.getItem('token');
        const method = this.ownerExists ? 'put' : 'post';

        // Include property_id in the request body
        const payload = {
          ...this.ownerData,
          property_id: this.selectedPropertyId
        };

        const response = await axios[method](
          `${this.baseUrl}/api/pms/property-owner/${this.selectedPropertyId}/`,
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          this.showNotification('success', 'Owner saved', response.data.message || 'Owner information saved.');
          this.ownerExists = true;
        }
      } catch (error) {
        console.error('Error saving owner:', error);
        // Handle both message and errors format from backend
        if (error.response?.data?.errors) {
          // Format validation errors in a user-friendly way
          const errors = error.response.data.errors;
          const fieldNames = {
            'full_legal_name': 'Full Legal Name',
            'primary_email': 'Primary Email',
            'phone_number': 'Phone Number',
            'date_of_birth': 'Date of Birth',
            'national_insurance_number': 'National Insurance Number',
            'address_line_1': 'Address Line 1',
            'address_line_2': 'Address Line 2',
            'city': 'City',
            'postal_code': 'Postal Code',
            'country': 'Country'
          };

          const errorMessages = Object.entries(errors)
            .map(([field, messages]) => {
              const friendlyField = fieldNames[field] || field;
              const messageText = Array.isArray(messages) ? messages.join(', ') : messages;
              return `${friendlyField}: ${messageText}`;
            })
            .join('<br>');

          this.showNotification('error', 'Validation errors', errorMessages);
        } else {
          this.showNotification('error', 'Save failed', error.response?.data?.message || 'Failed to save owner information. Please check your details and try again.');
        }
      } finally {
        this.savingOwner = false;
      }
    },

    async deleteOwnerInformation() {
      if (!confirm('Are you sure you want to delete this owner information?\n\nThis action cannot be undone.')) {
        return;
      }

      this.deletingOwner = true;

      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(
          `${this.baseUrl}/api/pms/property-owner/${this.selectedPropertyId}/`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          this.showNotification('success', 'Owner deleted', 'Owner information deleted successfully.');
          this.resetOwnerForm();
          this.ownerExists = false;
        }
      } catch (error) {
        console.error('Error deleting owner:', error);
        this.showNotification('error', 'Delete failed', 'Failed to delete owner information.');
      } finally {
        this.deletingOwner = false;
      }
    },

    validateOwnerForm() {
      // Only validate email - all other fields are optional
      if (!this.ownerData.primary_email || this.ownerData.primary_email.trim() === '') {
        this.showNotification('warning', 'Email required', 'Primary email is required.');
        return false;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.ownerData.primary_email)) {
        this.showNotification('warning', 'Invalid email', 'Please enter a valid email address (e.g., john.smith@example.com).');
        return false;
      }

      return true;
    },

    resetOwnerForm() {
      this.ownerData = {
        full_legal_name: '',
        primary_email: '',
        phone_number: '',
        date_of_birth: '',
        national_insurance_number: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        postal_code: '',
        country: 'GB'
      };
    }
  },
  
  mounted() {
    this.fetchProperties();
  }
};
</script>

<style scoped>
.dashboard-container {
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
}

/* Sidebar styles removed - using component Sidebar.vue */

/* Main Content */
.main-content {
  margin-left: 240px;
  flex: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Content area (after topbar) — W1 Refonte #11 — tighter, pro spacing */
.main-content > :not(.topbar) {
  padding: 20px 24px;
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}

/* Alerts */
.alert {
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 14px;
  font-weight: 500;
}

.alert-success {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.alert-error {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

/* Section Cards — W1 Refonte #11 — compact, pro */
.section-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.section-header {
  padding: 14px 20px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.section-header h2 {
  margin: 0 0 5px 0;
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
}

.section-description {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

/* Property Configuration Styles */
.property-selector {
  padding: 14px 20px;
}

.selector-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.property-select {
  width: 100%;
  max-width: 400px;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.property-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 14px;
  padding: 16px 20px;
}

.setting-group {
  display: flex;
  flex-direction: column;
}

.setting-group.full-width {
  grid-column: 1 / -1;
}

.setting-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.subsection-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin: 20px 0 10px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #e2e8f0;
}

.input-group {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.input-prefix,
.input-suffix {
  position: absolute;
  color: #6b7280;
  font-weight: 500;
  z-index: 2;
  pointer-events: none;
}

.input-prefix {
  left: 12px;
}

.input-suffix {
  right: 12px;
}

.setting-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.setting-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.input-prefix + .setting-input {
  padding-left: 45px;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.radio-option:hover {
  border-color: #667eea;
  background: #f8fafc;
}

.radio-option input[type="radio"] {
  margin: 0;
}

.radio-option input[type="radio"]:checked + .radio-content strong {
  color: #667eea;
}

.radio-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.radio-content strong {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  transition: color 0.3s ease;
}

.radio-description {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
  margin: 0;
}

.toggle-container {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
}

.toggle-switch input[type="checkbox"] {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e0;
  transition: 0.3s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: #667eea;
}

.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(24px);
}

.toggle-switch input:focus + .toggle-slider {
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
}

.toggle-label {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
}

.setting-description {
  font-size: 12px;
  color: #6b7280;
  margin: 8px 0 0 0;
  font-style: italic;
  line-height: 1.4;
}

/* Actions Section */
.actions-section {
  margin-top: 0;
  border-top: 1px solid #e2e8f0;
}

.actions-card {
  background: white;
  border-radius: 0;
}

.actions-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
}

.actions-info h3 {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
}

.warning-text {
  margin: 0;
  font-size: 14px;
  color: #d97706;
  font-weight: 500;
}

.success-text {
  margin: 0;
  font-size: 14px;
  color: #059669;
  font-weight: 500;
}

.actions-buttons {
  display: flex;
  gap: 12px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.btn-primary:hover:not(:disabled) {
  background: #5a67d8;
  transform: translateY(-1px);
}

.btn-secondary {
  background: #f59e0b;
  color: white;
  border-color: #f59e0b;
}

.btn-secondary:hover:not(:disabled) {
  background: #d97706;
  transform: translateY(-1px);
}

.btn-outline {
  background: transparent;
  color: #667eea;
  border-color: #667eea;
}

.btn-outline:hover:not(:disabled) {
  background: #667eea;
  color: white;
}

/* Month-specific badge */
.month-specific-badge {
  display: inline-block;
  background: #fbbf24;
  color: #78350f;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 8px;
  vertical-align: middle;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #64748b;
}

.empty-icon {
  font-size: 80px;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 10px 0;
  color: #1a202c;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
  max-width: 400px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }

  .main-content {
    margin-left: 0;
  }

  .main-content > :not(.topbar) {
    padding: 20px 15px;
  }

  .topbar {
    flex-direction: column;
    height: auto;
    padding: 15px 20px;
    gap: 10px;
  }

  .topbar-right {
    width: 100%;
    justify-content: space-between;
  }
  
  .settings-grid {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 20px;
  }
  
  .actions-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
  
  .actions-buttons {
    width: 100%;
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
  }
  
  .radio-group {
    gap: 8px;
  }
  
  .radio-option {
    padding: 10px 12px;
  }

  .cleaning-fees-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 15px;
  padding: 15px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

/* ========================================
   OWNER INFORMATION STYLES (Xero Integration - Milestone 1)
   ======================================== */

.owner-section {
  margin-top: 30px;
}

.owner-section .section-description {
  color: #64748b;
  margin-bottom: 20px;
}

.setting-label.required::after {
  content: ' *';
  color: #ef4444;
  font-weight: bold;
}

.helper-text {
  display: block;
  font-size: 12px;
  margin-top: 5px;
  color: #64748b;
}

.helper-text.warning {
  color: #f59e0b;
  font-weight: 500;
}

.field-error-message {
  display: block;
  font-size: 12px;
  margin-top: 8px;
  padding: 8px 12px;
  background: #fee2e2;
  border: 1px solid #fca5a5;
  border-radius: 6px;
  color: #dc2626;
  font-weight: 600;
}

.error-message-above-button {
  display: block;
  font-size: 14px;
  margin-top: 20px;
  margin-bottom: 15px;
  padding: 12px 16px;
  background: #fee2e2 !important;
  border: 2px solid #ef4444 !important;
  border-radius: 8px;
  color: #dc2626 !important;
  font-weight: 700 !important;
  text-align: center;
}

.input-error {
  border-color: #ef4444 !important;
  background-color: #fef2f2 !important;
}

.button-row {
  display: flex;
  gap: 12px;
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
}

.btn-danger {
  background: #ef4444;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

}

/* Tabs nav — exact CSS from invoxis-settings-v4-26.html mockup */
.settings-tabs {
  display: flex;
  gap: 2px;
  border-bottom: 1px solid #e8e6f4;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.settings-tab {
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #9896bb;
  cursor: pointer;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  background: transparent;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: 'Inter', sans-serif;
}
.settings-tab:hover {
  color: #4a4880;
}
.settings-tab.active {
  color: #5b4fcf;
  border-bottom-color: #5b4fcf;
}
.tab-future {
  font-size: 10px;
  font-weight: 600;
  color: #b7791f;
  background: rgba(253,203,110,0.2);
  border: 1px solid rgba(253,203,110,0.5);
  padding: 1px 7px;
  border-radius: 20px;
}

/* Notification Modal (aligned with reservationsView.vue) */
.notification-modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: notif-fadeIn 0.2s ease;
}
.notification-modal-container {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: notif-slideUp 0.3s ease;
}
@keyframes notif-fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes notif-slideUp {
  from { transform: translateY(20px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
}
.notification-modal-header {
  padding: 32px 24px 24px 24px;
  position: relative;
  text-align: center;
}
.notification-modal-header.success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
.notification-modal-header.error   { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
.notification-modal-header.warning { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
.notification-modal-header.info    { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); }
.notification-icon-circle {
  width: 72px; height: 72px;
  border-radius: 50%;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 3px solid rgba(255, 255, 255, 0.3);
}
.notification-icon { font-size: 36px; line-height: 1; color: white; font-weight: 400; }
.notification-modal-header .modal-close-btn {
  position: absolute;
  top: 12px; right: 12px;
  width: 32px; height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}
.notification-modal-header .modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}
.notification-modal-body {
  padding: 24px;
  text-align: center;
}
.notification-title {
  margin: 0 0 12px 0;
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}
.notification-message {
  margin: 0 0 20px 0;
  font-size: 16px;
  color: #6b7280;
  line-height: 1.5;
}
.notification-modal-footer {
  padding: 16px 24px 24px 24px;
  display: flex;
  justify-content: center;
}
.notification-btn {
  padding: 12px 40px;
  border-radius: 8px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  color: white;
  min-width: 120px;
}
.notification-btn.success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3); }
.notification-btn.error   { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3); }
.notification-btn.warning { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3); }
.notification-btn.info    { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3); }
.notification-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}
.notification-btn:active { transform: translateY(0); }
@media (max-width: 640px) {
  .notification-modal-container { width: 95%; margin: 20px; }
  .notification-title { font-size: 20px; }
  .notification-message { font-size: 14px; }
  .notification-icon-circle { width: 60px; height: 60px; }
  .notification-icon { font-size: 30px; }
}
</style>